import { ProductionOrder, ProductionOrderStatusColorMap, ProductionOrderStatusEnum } from '@app:types';
import { Badge, Box, Button, ButtonGroup, Tag, useDisclosure } from '@chakra-ui/react';
import { CreateOrderModal, ListView, OrderDetailsDrawer } from 'components';
import { SimplePageTemplate } from 'components/@common/layout';
import React from 'react';
import { RiDropboxLine } from 'react-icons/ri';
import { useStore } from 'store';
import { formatDate } from 'utils/helpers/formatting.helpers';

function Content() {
  const [{ orders }, dispatch] = useStore();

  const limit = 10;
  const [page, setPage] = React.useState(0);
  const [failedFetch, setFetchFailed] = React.useState(false);
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [selectedOrder, setSelectedOrder] = React.useState<ProductionOrder | null>(null);

  const fetchItems = React.useCallback(
    (forceRefresh: boolean = false) => {
      dispatch('orders', 'fetchPendingOrders', { page, limit, forceRefresh }, undefined, () => setFetchFailed(true));
    },
    [limit, page],
  );

  React.useEffect(() => {
    fetchItems();
  }, [page]);

  return (
    <>
      <Box bgColor='white' padding={3} w='100%' h='100%' borderColor='gray.200' borderWidth={1}>
        <Box w='100%' h='100%' overflowY={'auto'}>
          <ListView<ProductionOrder>
            title=''
            dataId='id'
            items={orders.items}
            totalItems={orders.totalItems}
            page={orders.page}
            limit={limit}
            isLoading={orders.isLoading}
            onChangePage={setPage}
            onRefresh={fetchItems.bind(null, true)}
            onSort={(key, val) => {
              dispatch('orders', 'sortPendingOrders', { by: key, mode: val, limit }, undefined, () => setFetchFailed(true));
            }}
            emptyMessage={'No pending orders'}
            errorMessage={failedFetch ? 'Error loading data' : undefined}
            headerVisible
            headerActions={[
              {
                label: 'Create order',
                colorScheme: 'blue',
                onClick: onOpen,
              },
            ]}
            onClickRow={(rowId) => {
              setSelectedOrder(orders.items.find((order) => order.id === rowId) || null);
            }}
            columns={[
              {
                key: 'trackingNumber',
                title: 'Tracking Number',
                props: {
                  width: '12%',
                },
              },
              {
                key: 'createdAt',
                title: 'Order Date',
                render: (data: string) => {
                  return formatDate(data, 'shortDate');
                },
                props: {
                  width: '12%',
                },
              },
              {
                key: 'metadata.origin',
                title: 'Channel',
                render: (data) => {
                  return <Tag>{data}</Tag>;
                },
                props: {
                  width: '12%',
                },
              },
              {
                key: 'content',
                title: 'Items',
                render: (data) => {
                  return <Tag>{data.length}</Tag>;
                },
                props: {
                  width: '12%',
                },
              },
              {
                key: 'status',
                title: 'Fulfillment status',
                render: (data: ProductionOrderStatusEnum) => {
                  return (
                    <Badge variant='outline' colorScheme={ProductionOrderStatusColorMap[data]}>
                      {data}
                    </Badge>
                  );
                },
              },
            ]}
          />
        </Box>
      </Box>
      <CreateOrderModal
        isOpen={isOpen}
        onCancel={onClose}
        onSubmitted={() => {
          onClose();
          fetchItems(true);
        }}
      />
      {orders.items.length > 0 && (
        <OrderDetailsDrawer order={selectedOrder || orders.items[0]} isOpen={!!selectedOrder} onClose={setSelectedOrder.bind(null, null)} />
      )}
    </>
  );
}

function ActionRender(props: { data: ProductionOrder }) {
  const { isOpen, onClose, onOpen } = useDisclosure();

  return (
    <>
      <ButtonGroup>
        <Button onClick={onOpen}>View</Button>
      </ButtonGroup>
      <OrderDetailsDrawer order={props.data} isOpen={isOpen} onClose={onClose} />
    </>
  );
}

export const ProductionOrders = () => {
  return (
    <SimplePageTemplate
      {...{
        title: 'Manage production orders',
        icon: RiDropboxLine,
      }}
      content={<Content />}
    />
  );
};
